import { FC, useState } from 'react';

import lang from '../../lib/language';
import { EpicMoversTabEnum } from '../../lib/models/fundamental/types';
import { isEmpty } from '../../lib/util/typeGuards';

import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';
import DiscoverButton from '../DiscoverButton/DiscoverButton';
import useIsMobile from '../hooks/useIsMobile';
import NoDataAvailable from '../NoDataAvailable/NoDataAvailable';

import Week52GainersAndLosersList from './components/Week52GainersAndLosersList/Week52GainersAndLosersList';
import { Week52GainersAndLosersProps } from './Week52GainersAndLosers.types';

import styles from './Week52GainersAndLosers.module.scss';

const Week52GainersAndLosers: FC<Week52GainersAndLosersProps> = ({
  topNHigh,
  topNLow,
  isPending,
  isReady,
  onViewAllClick,
  onSymbolClick,
}: Week52GainersAndLosersProps): JSX.Element => {
  const { isMobile } = useIsMobile();
  const [selectedTab, setSelectedTab] = useState<EpicMoversTabEnum>(EpicMoversTabEnum.TopGainers);
  const isTopGainersSelected = selectedTab === EpicMoversTabEnum.TopGainers;
  const isTopLoserSelected = selectedTab === EpicMoversTabEnum.TopLosers;

  const hasNoAnyData = isEmpty(topNHigh) && isEmpty(topNLow) && !isPending;

  return (
    <>
      <div className={styles.titleWrapper}>
        <div className={styles.titleRow}>
          <div>
            <span className={GlobalClassesEnum.CommonDiscoverTitle}>
              {lang.commonWeek52GainersAndLosers()}
            </span>
            <span className={styles.subtitle}>
              {lang.commonWeek52GainersAndLosersStockMakingHighLow()}
            </span>
          </div>
          {!hasNoAnyData && (
            isMobile ? (
              <Button
                variant={ButtonTypesEnum.Link}
                onClick={() => onViewAllClick(isTopGainersSelected)}
                className={styles.viewAll}
              >
                {lang.commonViewAll()}
              </Button>
            ) : (
              <DiscoverButton
                disabled={isPending}
                onClick={() => onViewAllClick(isTopGainersSelected)}
              />
            )
          )}
        </div>
        {isMobile && !hasNoAnyData && (
          <div className={styles.tabsWrapper}>
            <div className={styles.tabs}>
              <Button
                className={isTopGainersSelected ? styles.tabActive : styles.tab}
                disabled={isPending}
                onClick={() => setSelectedTab(EpicMoversTabEnum.TopGainers)}
              >
                {lang.commonEpicMoversGainers()}
              </Button>

              <Button
                className={isTopLoserSelected ? styles.tabActive : styles.tab}
                disabled={isPending}
                onClick={() => setSelectedTab(EpicMoversTabEnum.TopLosers)}
              >
                {lang.commonEpicMoversLosers()}
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className={isMobile ? styles.wrapperMobile : styles.wrapper}>
        <div className={styles.listWrapper}>
          {hasNoAnyData
            ? (
              <NoDataAvailable
                className={styles.noDataToDisplay}
                isNoDataIcon
                title={lang.commonDiscover52WeekNoDataAll()}
              />
            )
            : (
              <div className={styles.list}>
                {(!isMobile || isTopGainersSelected)
                  && (
                    <Week52GainersAndLosersList
                      title={lang.commonEpicMoversGainers()}
                      data={topNHigh}
                      isReady={isReady}
                      onSymbolClick={symbol => onSymbolClick(symbol, true)}
                    />
                  )}
                {!isMobile && <div className={styles.verticalSpace} />}
                {(!isMobile || isTopLoserSelected)
                  && (
                    <Week52GainersAndLosersList
                      title={lang.commonEpicMoversLosers()}
                      data={topNLow}
                      isReady={isReady}
                      onSymbolClick={symbol => onSymbolClick(symbol, false)}
                    />
                  )}
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default Week52GainersAndLosers;
